import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';

import { ImageDefinitionMapper } from './ai/image.definition-mapper';
import { ImageElementHandler } from './ai/image.element-handler';
import { generateImageFormField } from './generation/form-field';
import { ImageComponent } from './image.component';
import { type ImagePropsModel } from './image.models';
import { ImagePropsBuilder } from './image.props-builder';
import { DEFAULT_HEIGHT, DEFAULT_WIDTH, getPropsConfig } from './image.props-config';
import { imageV2Migration } from './migrations/002-image.migration';
import { imageV3PaddingToMarginMigration } from './migrations/003-image-padding-to-margin.migration';

export { transformToImage } from './image.transforms';
export type ImageDef = WithProps<ImagePropsModel> & WithIoConfig;
export type ImageElement = CanvasElementFrom<'image'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    image: ImageDef;
  }
}

registerElementDefinition({
  type: 'image',
  componentType: ImageComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Image',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: true },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: ImagePropsBuilder,
  generation: {
    formField: generateImageFormField,
  },
  toolbar: ['triggerClick'],
  tags: ['File Upload', 'Photo'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: { type: 'input' },
  features: {
    ai: {
      description: true,
    },
  },
  ai: {
    name: 'image',
    handler: () => ImageElementHandler,
    definitionMapper: () => ImageDefinitionMapper,
  },
  version: 3,
  migrations: {
    2: imageV2Migration,
    3: imageV3PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'URL', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        schema: {
          columns: [
            {
              columnType: 'String', // image URL
              dataType: 'String',
              name: '', // for non-object value schema, this should be empty
              isProfilePicture: false,
            },
          ],
          id: '',
          name: 'Image',
        },
      },
    ],
  };
}

function getDefaults(): PropsOf<'image'> {
  return {
    content: {
      imageSource: {
        enabled: true,
        url: '',
      },
      options: {
        allowEdit: false,
        altText: '',
        behavior: 'cover',
        emptyText: 'No image',
        emptyThumbnail: 'custom-text',
        forceRatio: 'none',
        forceRatioCustom: '1:1',
        initialsColumn: '',
        readonly: false,
        unlimitedUpload: false,
        uploadMax: 1,
      },
      title: {
        enabled: false,
        label: 'Image',
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      image: {
        offset: {
          align: 'middle left',
          margin: '$t.spacingInput',
        },
      },
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
      },
      labelBlock: {
        fontSize: '$t.fontSize',
        fontFamily: '$t.fontFamily',
        fontWeight: '$t.labelFontWeight',
        fontStyle: [],
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      outline: {
        color: { color1: '$t.outline', selectorType: 'solid' },
        width: '0px',
        radius: '$t.radius',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'image'> {
  return {
    content: {
      title: {
        width: '35%',
      },
    },
  };
}
